import React, { useCallback } from 'react';
import { Box, TableCell, Tooltip, Typography } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { FilterColumn } from '../../../../types/filters';
import { useFilterHeaderMismatch } from './FilterHeaderMismatchContext';

export interface FilterTableHeadCellProps {
  columnId: string;
  label: React.ReactNode;
  align?: 'left' | 'center' | 'right';
  /** Colunas de filtro da página (mesma lista passada ao useDynamicFilters). */
  filterColumns: FilterColumn[];
  /** Retornado pelo hook useDynamicFilters. */
  openFilterForColumn: (columnValue: string) => boolean;
  /**
   * Se false, cabeçalho estático (ex.: Ações).
   * Se true, clique tenta abrir o filtro ou exibe aviso de não correspondência.
   */
  filterable?: boolean;
  /**
   * Chave em FilterColumn.value. Se omitido e filterable, usa columnId.
   */
  filterKey?: string;
}

const labelToString = (label: React.ReactNode, columnId: string): string => {
  if (typeof label === 'string' || typeof label === 'number') return String(label);
  return columnId;
};

export const FilterTableHeadCell: React.FC<FilterTableHeadCellProps> = ({
  columnId,
  label,
  align = 'left',
  filterColumns,
  openFilterForColumn,
  filterable = true,
  filterKey,
}) => {
  const { showFilterColumnMismatch } = useFilterHeaderMismatch();

  const resolvedKey = filterKey ?? columnId;

  const handleClick = useCallback(() => {
    if (!filterable) return;
    const ok = openFilterForColumn(resolvedKey);
    if (!ok) {
      showFilterColumnMismatch({
        headerLabel: labelToString(label, columnId),
        columnId,
        resolvedFilterKey: resolvedKey,
        filterColumns,
      });
    }
  }, [
    filterable,
    openFilterForColumn,
    resolvedKey,
    showFilterColumnMismatch,
    columnId,
    filterColumns,
    label,
  ]);

  if (!filterable) {
    return (
      <TableCell align={align}>
        {label}
      </TableCell>
    );
  }

  return (
    <TableCell align={align} sx={{ userSelect: 'none' }}>
      <Tooltip title="Filtrar por esta coluna" enterDelay={400}>
        <Box
          component="button"
          type="button"
          onClick={handleClick}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            cursor: 'pointer',
            border: 'none',
            background: 'none',
            padding: 0,
            margin: 0,
            font: 'inherit',
            color: 'inherit',
            textAlign: align,
            maxWidth: '100%',
            '&:hover': {
              opacity: 0.85,
            },
          }}
        >
          <FilterListIcon
            sx={{ fontSize: 16, opacity: 0.7, flexShrink: 0, color: 'inherit' }}
            aria-hidden
          />
          <Typography
            component="span"
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: 'inherit',
            }}
          >
            {label}
          </Typography>
        </Box>
      </Tooltip>
    </TableCell>
  );
};
